import type { NextRequest } from 'next/server';

import type { LogEventCategoryType } from './logEvents';

export const LogLevel = {
  TRACE: 'TRACE',
  DEBUG: 'DEBUG',
  INFO: 'INFO',
  WARN: 'WARN',
  ERROR: 'ERROR',
  SILENT: 'SILENT',
} as const;
export type LogLevelDesc = keyof typeof LogLevel;
export const LogCategory = {
  AUTH: 'auth',
  PERMIT: 'permit',
  WORKFLOW: 'workflow',
  API: 'api',
  FORM: 'form',
  USER: 'user',
  STORAGE: 'storage',
  LEGACY: 'legacy',
  INSTRUMENTATION: 'instrumentation',
  AI: 'ai',
} as const;

// Guidelines for log levels:
// TRACE: Detailed information about code execution path
// DEBUG: Debugging information, variable states
// INFO: Notable events that are part of normal operation
// WARN: Warning messages for potentially harmful situations
// ERROR: Error events that might still allow the application to continue running
// SILENT: Turn off logging

export const LogEvent = {
  // Auth events
  USER_LOGIN: 'USER_LOGIN',
  USER_LOGOUT: 'USER_LOGOUT',
  AUTH_FAILED: 'AUTH_FAILED',
  UNAUTHORIZED_ACCESS: 'UNAUTHORIZED_ACCESS',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  FAILED_LOGIN: 'FAILED_LOGIN',

  // Permit events
  PERMIT_CREATED: 'PERMIT_CREATED',
  PERMIT_UPDATED: 'PERMIT_UPDATED',
  PERMIT_COMPLETED: 'PERMIT_COMPLETED',
  PERMIT_CANCELLED: 'PERMIT_CANCELLED',

  // Workflow events
  WORKFLOW_STEP_STARTED: 'WORKFLOW_STEP_STARTED',
  WORKFLOW_STEP_COMPLETED: 'WORKFLOW_STEP_COMPLETED',
  WORKFLOW_STATE_CHANGED: 'WORKFLOW_STATE_CHANGED',

  // API events
  API_REQUEST: 'API_REQUEST',
  API_RESPONSE: 'API_RESPONSE',
  API_ERROR: 'API_ERROR',

  // Storage events
  STORAGE_BUCKET_ACCESSED: 'STORAGE_BUCKET_ACCESSED',
  STORAGE_FILE_UPLOADED: 'STORAGE_FILE_UPLOADED',
  STORAGE_FILE_DELETED: 'STORAGE_FILE_DELETED',
  STORAGE_FILE_DOWNLOAD_URL_GENERATED: 'STORAGE_FILE_DOWNLOAD_URL_GENERATED',
  STORAGE_FILE_STREAMING: 'STORAGE_FILE_STREAMING',
  STORAGE_FILES_LISTED: 'STORAGE_FILES_LISTED',
  STORAGE_FILE_QUARANTINE_CHECK: 'STORAGE_FILE_QUARANTINE_CHECK',
  STORAGE_FILE_VALIDATION_FAILED: 'STORAGE_FILE_VALIDATION_FAILED',
  STORAGE_FILE_UPLOAD_FAILED: 'STORAGE_FILE_UPLOAD_FAILED',
  STORAGE_ERROR: 'STORAGE_ERROR',

  //GeoFence events
  GEOFENCE_LOCATION_ERROR: 'GEOFENCE_LOCATION_ERROR',

  // Site (location) admin — API audit
  SITE_CREATED: 'SITE_CREATED',
  SITE_UPDATED: 'SITE_UPDATED',
  SITE_DELETED: 'SITE_DELETED',

  // Form events
  FORM_SUBMITTED: 'FORM_SUBMITTED',
  FORM_DRAFT_SAVED: 'FORM_DRAFT_SAVED',
  FORM_VALIDATION_FAILED: 'FORM_VALIDATION_FAILED',

  // User events
  USER_CREATED: 'USER_CREATED',
  USER_UPDATED: 'USER_UPDATED',
  USER_DELETED: 'USER_DELETED',
  ADDITION_OF_USER_ACCOUNT: 'Addition of User account',
  MODIFICATION_OF_ACCOUNT_PRIVILEGES: 'Modification of Account Privileges',
  INVITE_ACCEPTED: 'INVITE_ACCEPTED',
  ACCOUNT_DETAILS: 'ACCOUNT_DETAILS',
  USER_DETAILS: 'USER_DETAILS',
  EXISTING_USER: 'EXISTING_USER',
  LINKING_EXISTING_USER: 'LINKING_EXISTING_USER',
  NO_ACCOUNT_FOUND: 'NO_ACCOUNT_FOUND',

  // Navigation events
  MODULE_LOOKUP_FAILED: 'MODULE_LOOKUP_FAILED',
  FALLBACK_HOMEPAGE: 'FALLBACK_HOMEPAGE',
  MISSING_SUB: 'MISSING_SUB',

  // Database/Data Access events
  ZEEBE_OPERATION_FAILED: 'ZEEBE_OPERATION_FAILED',
  GRAPHQL_QUERY_FAILED: 'GRAPHQL_QUERY_FAILED',
  DATA_ACCESS_INITIALIZATION_FAILED: 'DATA_ACCESS_INITIALIZATION_FAILED',

  // Instrumentation
  INSTRUMENTATION: 'INSTRUMENTATION',

  // Legacy logging events (for deprecated methods)
  LEGACY_LOG: 'LEGACY_LOG',
} as const;

export type LogEventType = (typeof LogEvent)[keyof typeof LogEvent];
export type LogCategoryType = (typeof LogCategory)[keyof typeof LogCategory];

// Standard log metadata that should be included in every log
export type LogMetadata = {
  category: LogCategoryType;
  event: LogEventType;
  correlationId?: string; // For tracking related events
  workflowId?: string;
  permitId?: string;
  userId?: string;
  duration?: number; // For performance tracking
  error?: Error;
  request?: NextRequest;
  eventCategory?: LogEventCategoryType;
  rawBody?: string;
  [key: string]: unknown;
};

export const MAX_LOG_BODY_LENGTH = 2000; // 2 KB
